import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getUserPreferences, postListingsBasicRecommended, postUserPreferences } from "api/methods";
import { Models, PostRequest } from "api/types";
import {
  fromSavedPreferences,
  toSaveRequest,
} from "modules/personalisation/utils/personalisationMapping";
import { useUser } from "modules/user/useUser/useUser";

export type Advisor = { id: string; label: string; avatarUrl: string };
export type ConcernItem = Models<"DisplayedConcern">;

export type TriState<T> =
  | { state: "unanswered" }
  | { state: "answered"; value: T }
  | { state: "skipped" };

export type PersonalisationData = {
  advisor: TriState<Advisor>;
  concerns: TriState<string[]>;
  journey: TriState<string>;
  journeyDetails: TriState<string>;
  heritage: TriState<string>;
  skinColor: TriState<string>;
  skinSun: TriState<string>;
  skinBalance: TriState<string>;
};

type PersonalisationDataContextValue = PersonalisationData & {
  setAdvisorAnswered: (advisor: Advisor) => Promise<void>;
  skipAdvisor: () => Promise<void>;

  setConcernsAnswered: (selections: string[]) => Promise<void>;
  skipConcerns: () => Promise<void>;

  setJourneyAnswered: (journeyId: string) => Promise<void>;
  skipJourney: () => Promise<void>;

  setHeritageAnswered: (heritageId: string) => Promise<void>;
  skipHeritage: () => Promise<void>;

  setSkinColorAnswered: (skinColorId: string) => Promise<void>;
  skipSkinColor: () => Promise<void>;

  setSkinSunAnswered: (skinSunId: string) => Promise<void>;
  skipSkinSun: () => Promise<void>;

  setSkinBalanceAnswered: (skinBalanceId: string) => Promise<void>;
  skipSkinBalance: () => Promise<void>;

  setJourneyDetailsAnswered: (text: string) => Promise<void>;
  skipJourneyDetails: () => Promise<void>;

  advisorIdentity: {
    name: string;
    avatarUrl: string;
  };

  user: Exclude<ReturnType<typeof useUser>["user"], undefined>;
};

import AdonisAvatar from "../components/adonis-advisor-preferences.png";
import AriaAvatar from "../components/aria-advisor-preferences.png";

const PersonalisationDataContext = createContext<PersonalisationDataContextValue | null>(null);

export const usePersonalisationData = () => {
  const ctx = useContext(PersonalisationDataContext);
  if (!ctx) throw new Error("usePersonalisationData must be used within provider");
  return ctx;
};

export const PersonalisationDataProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const [advisor, setAdvisor] = useState<TriState<Advisor>>({
    state: "unanswered",
  });
  const [concerns, setConcerns] = useState<TriState<string[]>>({
    state: "unanswered",
  });
  const [journey, setJourney] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [journeyDetails, setJourneyDetails] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [heritage, setHeritage] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinColor, setSkinColor] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinSun, setSkinSun] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [skinBalance, setSkinBalance] = useState<TriState<string>>({
    state: "unanswered",
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Load existing preferences
  const { data: savedPreferences } = useQuery({
    ...getUserPreferences.keyed(),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Initialize state from saved preferences when data is loaded
  useEffect(() => {
    if (savedPreferences && !isInitialized) {
      const loadedData = fromSavedPreferences(savedPreferences);

      setAdvisor(loadedData.advisor);
      setConcerns(loadedData.concerns);
      setJourney(loadedData.journey);
      setJourneyDetails(loadedData.journeyDetails);
      setHeritage(loadedData.heritage);
      setSkinColor(loadedData.skinColor);
      setSkinSun(loadedData.skinSun);
      setSkinBalance(loadedData.skinBalance);

      setIsInitialized(true);
    }
  }, [savedPreferences, isInitialized]);

  const savePreferences = useMutation({
    mutationFn: (data: PostRequest<"/user/preferences">) => postUserPreferences(data),
  });

  const fetchBasicRecommendations = useMutation({
    mutationFn: () => postListingsBasicRecommended(undefined),
  });

  const saveCurrentState = useCallback(
    async (updatedData: Partial<PersonalisationData>) => {
      try {
        const currentData = {
          advisor,
          concerns,
          journey,
          journeyDetails,
          heritage,
          skinColor,
          skinSun,
          skinBalance,
          ...updatedData,
        };
        const backendRequest = toSaveRequest(currentData);
        await savePreferences.mutateAsync(backendRequest);
        // Invalidate the cached preferences so downstream consumers
        // (e.g. Shop "Just for you" tab) pick up the new values.
        queryClient.invalidateQueries({ queryKey: getUserPreferences.key() });
      } catch (error) {
        console.error("Failed to save preferences:", error);
      }
    },
    [
      advisor,
      concerns,
      journey,
      journeyDetails,
      heritage,
      skinColor,
      skinSun,
      skinBalance,
      savePreferences,
      queryClient,
    ],
  );

  if (!user) {
    throw new Error(`Personalisation flow loaded without a logged in user`);
  }

  const value = useMemo(() => {
    const isMale = user?.gender?.trim().toLowerCase() === "male";
    const defaultAdvisorIdentity = isMale
      ? {
          name: "Adonis",
          avatarUrl: AdonisAvatar,
        }
      : {
          name: "Aria",
          avatarUrl: AriaAvatar,
        };
    return {
      user,
      advisor,
      concerns,
      journey,
      heritage,
      journeyDetails,
      skinColor,
      skinSun,
      skinBalance,

      setAdvisorAnswered: async (next: Advisor) => {
        const newState = { state: "answered", value: next } as const;
        setAdvisor(newState);
        await saveCurrentState({ advisor: newState });
      },
      skipAdvisor: async () => {
        const newState = { state: "skipped" } as const;
        setAdvisor(newState);
        await saveCurrentState({ advisor: newState });
      },

      setConcernsAnswered: async (next: string[]) => {
        const newState = { state: "answered", value: next } as const;
        setConcerns(newState);
        await saveCurrentState({ concerns: newState });
        // Best-effort attempt — the preference save uses FireAndForgetAsync
        // on the backend, so it may not be committed yet. The
        // PreparingScreenContainer retries this as a safety net.
        try {
          await fetchBasicRecommendations.mutateAsync();
        } catch (error) {
          console.warn("Initial recommendation generation failed (will retry later):", error);
        }
      },
      skipConcerns: async () => {
        const newState = { state: "skipped" } as const;
        setConcerns(newState);
        await saveCurrentState({ concerns: newState });
      },

      setJourneyAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setJourney(newState);
        await saveCurrentState({ journey: newState });
      },
      skipJourney: async () => {
        const newState = { state: "skipped" } as const;
        setJourney(newState);
        await saveCurrentState({ journey: newState });
      },

      setHeritageAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setHeritage(newState);
        await saveCurrentState({ heritage: newState });
      },
      skipHeritage: async () => {
        const newState = { state: "skipped" } as const;
        setHeritage(newState);
        await saveCurrentState({ heritage: newState });
      },

      setSkinColorAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setSkinColor(newState);
        await saveCurrentState({ skinColor: newState });
      },
      skipSkinColor: async () => {
        const newState = { state: "skipped" } as const;
        setSkinColor(newState);
        await saveCurrentState({ skinColor: newState });
      },

      setSkinSunAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setSkinSun(newState);
        await saveCurrentState({ skinSun: newState });
      },
      skipSkinSun: async () => {
        const newState = { state: "skipped" } as const;
        setSkinSun(newState);
        await saveCurrentState({ skinSun: newState });
      },

      setSkinBalanceAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setSkinBalance(newState);
        await saveCurrentState({ skinBalance: newState });
      },
      skipSkinBalance: async () => {
        const newState = { state: "skipped" } as const;
        setSkinBalance(newState);
        await saveCurrentState({ skinBalance: newState });
      },

      setJourneyDetailsAnswered: async (next: string) => {
        const newState = { state: "answered", value: next } as const;
        setJourneyDetails(newState);
        await saveCurrentState({ journeyDetails: newState });
      },
      skipJourneyDetails: async () => {
        const newState = { state: "skipped" } as const;
        setJourneyDetails(newState);
        await saveCurrentState({ journeyDetails: newState });
      },

      advisorIdentity:
        advisor.state === "answered"
          ? {
              name: advisor.value.label,
              avatarUrl: advisor.value.label === "Adonis" ? AdonisAvatar : AriaAvatar,
            }
          : defaultAdvisorIdentity,
    };
  }, [
    advisor,
    concerns,
    journey,
    heritage,
    journeyDetails,
    skinColor,
    skinSun,
    skinBalance,
    user,
    saveCurrentState,
    fetchBasicRecommendations,
  ]);

  return (
    <PersonalisationDataContext.Provider value={value}>
      {children}
    </PersonalisationDataContext.Provider>
  );
};
